// src/modules/app-views/crm/pages/Boards/tabs/AgendaTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Calendar, Card, Col, Empty, Row, Select, Splitter, Tooltip } from 'antd';
import { CalendarOutlined, DeleteOutlined, EditOutlined, HomeOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';
import appService from 'services/appService';
import agendasService from 'modules/app-views/crm/agendasService';
import AgendaEventModal, { EVENT_COLOR_MAP } from './AgendaEventModal';
import { getStorageModuleItem, setStorageModuleItem } from 'stores/module';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AgendaTab = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { onReady } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const initialLoadDone = useRef(false);
  const calendarRef = useRef(null);
  const skipSelectRef = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarDate, setCalendarDate] = useState(dayjs());
  const [yearSelectOpen, setYearSelectOpen] = useState(false);
  const [monthSelectOpen, setMonthSelectOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [calendarCompact, setCalendarCompact] = useState(
    () => !!getStorageModuleItem('agendaCalendarCompact')
  );
  const todayDate = dayjs();

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Load from API
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    (async () => {
      try {
        const data = await agendasService.fetchAll();
        // Normalize date from backend (Date object) to YYYY-MM-DD string for UI
        const normalized = data.map(evt => ({
          ...evt,
          date: evt.date ? dayjs(evt.date).format('YYYY-MM-DD') : evt.date,
        }));
        setEvents(normalized);
      } catch {
        // ignore fetch errors on initial load
      } finally {
        onReady?.();
      }
    })();
  }, []);

  // Auto-scroll to today in compact mode when viewing current month
  useEffect(() => {
    if (!calendarCompact || isMobile) return;
    const isCurrentMonth = calendarDate.isSame(todayDate, 'month');
    if (!isCurrentMonth) return;

    scrollToToday();
  }, [calendarCompact, calendarDate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSelectDate = useCallback(
    date => {
      // Skip if click came from an event item (handled by handleClickEvent)
      if (skipSelectRef.current) {
        skipSelectRef.current = false;
        return;
      }

      const isBeforeToday = date.isBefore(todayDate, 'day');

      // Past dates: click is handled per-event inside fullCellRender
      if (isBeforeToday) return;

      // Future or today: open modal for new event
      const dateKey = date.format('YYYY-MM-DD');
      setIsViewOnly(false);
      setEditingEvent(null);
      setSelectedDate(dateKey);
      setShowModal(true);
    },
    [todayDate]
  );

  const handleClickEvent = useCallback((evt, viewOnly) => {
    skipSelectRef.current = true;
    setIsViewOnly(viewOnly);
    setEditingEvent(evt);
    setSelectedDate(null);
    setShowModal(true);
  }, []);

  const handleCalendarDateChange = useCallback(date => {
    setCalendarDate(date);
  }, []);

  const handleYearChange = useCallback(
    newYear => {
      const newDate = calendarDate.year(newYear);
      setCalendarDate(newDate);
      setYearSelectOpen(false);
    },
    [calendarDate]
  );

  const handleMonthChange = useCallback(
    newMonth => {
      const newDate = calendarDate.month(newMonth);
      setCalendarDate(newDate);
      setMonthSelectOpen(false);
    },
    [calendarDate]
  );

  const scrollToToday = useCallback(() => {
    requestAnimationFrame(() => {
      const container = calendarRef.current;
      if (!container) return;
      const todayCell = container.querySelector('.ant-picker-cell-today');
      if (!todayCell) return;
      const tbody = container.querySelector('.ant-picker-content tbody');
      if (!tbody) return;
      tbody.scrollTop = todayCell.offsetTop - tbody.offsetTop - 80;
    });
  }, []);

  const handleGoToToday = useCallback(() => {
    const today = dayjs();
    setCalendarDate(today);

    // Scroll to today in compact mode (after state update + re-render)
    if (calendarCompact && !isMobile) {
      setTimeout(scrollToToday, 50);
    }
  }, [calendarCompact, isMobile, scrollToToday]);

  const handleEditEvent = useCallback(event => {
    setEditingEvent(event);
    setSelectedDate(null);
    setShowModal(true);
  }, []);

  const handleDeleteEvent = useCallback(id => {
    appService.modal({
      flag: 'confirm',
      title: 'delete',
      content: 'delete_event_confirm',
      okText: 'yes',
      cancelText: 'no',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await agendasService.remove(id);
          setEvents(prev => prev.filter(e => (e._id || e.uid) !== id));
        } catch {
          // ignore delete errors
        }
      },
    });
  }, []);

  const handleSplitterResize = useCallback(sizes => {
    // sizes[1] is the calendar panel width in pixels
    const compact = sizes[1] < 768;
    setCalendarCompact(compact);
    setStorageModuleItem('agendaSplitterSize', sizes[0]);
    setStorageModuleItem('agendaCalendarCompact', compact);
  }, []);

  const handleSaveEvent = useCallback(async values => {
    try {
      const id = values._id || values.uid;

      if (id) {
        // Edit existing event
        const updated = await agendasService.save({
          _id: id,
          title: values.title,
          date: values.date,
          startTime: values.startTime,
          endTime: values.endTime,
          eventType: values.eventType,
          notes: values.notes,
        });

        if (updated) {
          // Normalize date from backend
          const normalizedDate = updated.date
            ? dayjs(updated.date).format('YYYY-MM-DD')
            : values.date;

          setEvents(prev =>
            prev.map(e =>
              (e._id || e.uid) === id
                ? { ...e, ...updated, date: normalizedDate }
                : e
            )
          );
        }
      } else {
        // Create new event
        const created = await agendasService.save({
          title: values.title,
          date: values.date,
          startTime: values.startTime,
          endTime: values.endTime,
          eventType: values.eventType,
          notes: values.notes || '',
        });

        if (created) {
          // Normalize date from backend
          const normalizedDate = created.date
            ? dayjs(created.date).format('YYYY-MM-DD')
            : values.date;

          setEvents(prev => [...prev, { ...created, date: normalizedDate }]);
        }
      }
    } catch {
      // ignore save errors
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Group events by date for the sidebar (upcoming events, sorted)
  const upcomingEvents = buildUpcomingEvents(events);

  // Build a lookup map for calendar cell rendering
  const eventsByDate = buildEventsByDate(events);
  const isCompact = isMobile || calendarCompact;

  const sidebarContent = (
    <div style={{ padding: isMobile ? 0 : '0 16px' }}>
      <h4 style={{ marginBottom: 16 }}>{translateX('upcoming_events')}</h4>

      {upcomingEvents.length === 0 ? (
        <Empty
          description={translateX('no_events_yet')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '20px 0' }}
        />
      ) : (
        upcomingEvents.map(group => (
          <div key={group.date} style={{ marginBottom: 16 }}>
            <h5 style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <CalendarOutlined />
              <span>{dayjs(group.date).format('DD MMM YYYY')}</span>
            </h5>

            {group.items.map(evt => (
              <div key={evt._id || evt.uid} className="agenda-list-item">
                <div
                  className="d-flex"
                  style={{ alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}
                >
                  <Badge color={EVENT_COLOR_MAP[evt.eventType] || 'blue'} />
                  <div style={{ minWidth: 0 }}>
                    <h5 className="mb-0" style={{ fontSize: 13 }}>
                      {evt.title}
                    </h5>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      {formatTimeRange(evt.startTime, evt.endTime)}
                    </span>
                  </div>
                </div>
                <div className="agenda-list-actions">
                  <Tooltip title={translateX('edit')}>
                    <EditOutlined
                      style={{ fontSize: 14, color: 'rgba(0,0,0,0.45)', cursor: 'pointer' }}
                      onClick={() => handleEditEvent(evt)}
                    />
                  </Tooltip>
                  <Tooltip title={translateX('delete')}>
                    <DeleteOutlined
                      style={{ fontSize: 14, color: 'rgba(0,0,0,0.45)', cursor: 'pointer' }}
                      onClick={() => handleDeleteEvent(evt._id || evt.uid)}
                    />
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );

  const calendarContent = (
    <div ref={calendarRef}>
      <Card
        variant="borderless"
        style={{
          boxShadow:
            '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
        }}
      >
        {/* Header: Year and Month selectors */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <Select
            value={calendarDate.year()}
            onChange={handleYearChange}
            open={yearSelectOpen}
            onOpenChange={setYearSelectOpen}
            style={{ width: 100 }}
            options={[
              { label: (dayjs().year() - 1).toString(), value: dayjs().year() - 1 },
              { label: dayjs().year().toString(), value: dayjs().year() },
              { label: (dayjs().year() + 1).toString(), value: dayjs().year() + 1 },
            ]}
          />
          <Select
            value={calendarDate.month()}
            onChange={handleMonthChange}
            open={monthSelectOpen}
            onOpenChange={setMonthSelectOpen}
            style={{ width: 120 }}
            options={Array.from({ length: 12 }, (_, i) => ({
              label: dayjs().month(i).format('MMMM'),
              value: i,
            }))}
          />
          <Tooltip title={translateX('today')}>
            <Button
              type="text"
              size="small"
              icon={<HomeOutlined />}
              onClick={handleGoToToday}
              disabled={calendarDate.isSame(dayjs(), 'month')}
            />
          </Tooltip>
        </div>

        {/* Calendar */}
        <Calendar
          className={`agenda-calendar${isCompact ? ' agenda-calendar-mobile' : ''}${calendarCompact && !isMobile ? ' agenda-calendar-compact-scroll' : ''}`}
          value={calendarDate}
          onChange={handleCalendarDateChange}
          onSelect={handleSelectDate}
          fullCellRender={(current, info) => {
            if (info.type !== 'date') return info.originNode;
            const dateKey = current.format('YYYY-MM-DD');
            const isPast = current.isBefore(todayDate, 'day');
            const isToday = current.isSame(todayDate, 'day');
            const isSaturday = current.day() === 6;
            const isSunday = current.day() === 0;
            const dayEvents = eventsByDate[dateKey];
            const hasEvents = dayEvents && dayEvents.length > 0;

            return (
              <div
                className={`ant-picker-cell-inner ant-picker-calendar-date${isPast ? ' past-date' : ''}${hasEvents && isPast ? ' past-with-events' : ''}${!isToday && isSaturday ? ' weekend-saturday' : ''}${!isToday && isSunday ? ' weekend-sunday' : ''}`}
              >
                <div
                  className="ant-picker-calendar-date-value"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 12px',
                  }}
                >
                  <div>
                    {hasEvents && (
                      <Tooltip title={translateX('day_appointments')}>
                        <Badge
                          count={dayEvents.length}
                          style={{ backgroundColor: 'var(--ant-color-primary)' }}
                        />
                      </Tooltip>
                    )}
                  </div>
                  <span>
                    {isCompact && (
                      <span className="text-muted" style={{ marginRight: 6 }}>
                        {current.format('ddd')}
                      </span>
                    )}
                    {current.date()}
                  </span>
                </div>
                <div className="ant-picker-calendar-date-content">
                  {hasEvents && (
                    <ul className="calendar-event">
                      {dayEvents.map(evt => (
                        <li
                          key={evt._id || evt.uid}
                          className="clickable-event-item"
                          onClick={e => {
                            e.stopPropagation();
                            handleClickEvent(evt, isPast);
                          }}
                        >
                          <Badge
                            className="mt-1 mb-1 ml-2 mr-2"
                            color={EVENT_COLOR_MAP[evt.eventType] || 'blue'}
                            text={evt.title}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          }}
        />
      </Card>
    </div>
  );

  const renderLayout = () => {
    if (isMobile) {
      return (
        <Row gutter={24}>
          <Col xs={24}>{sidebarContent}</Col>
          <Col xs={24}>{calendarContent}</Col>
        </Row>
      );
    }

    return (
      <Splitter style={{ flex: 1, minHeight: 0 }} onResize={handleSplitterResize}>
        <Splitter.Panel
          defaultSize={getStorageModuleItem('agendaSplitterSize') || '25%'}
          min="15%"
          max="50%"
        >
          <div style={{ height: '100%', overflowY: 'auto', padding: '16px 0' }}>
            {sidebarContent}
          </div>
        </Splitter.Panel>
        <Splitter.Panel>
          <div style={{ height: '100%', overflowY: 'auto', padding: '16px 0' }}>
            {calendarContent}
          </div>
        </Splitter.Panel>
      </Splitter>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={!isMobile ? { height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' } : undefined}>
      <style>{`
        .agenda-calendar .ant-picker-calendar-header {
          display: none !important;
        }
        .agenda-calendar .ant-picker-calendar-date-content {
          height: 86px !important;
          overflow-y: auto;
        }
        .agenda-calendar .ant-picker-cell-selected .ant-picker-cell-inner {
          background: transparent !important;
        }
        .agenda-calendar .ant-picker-cell-today .ant-picker-cell-inner::before {
          display: none !important;
        }
        .agenda-calendar .ant-picker-cell-today .ant-picker-cell-inner {
          background-color: rgba(255, 153, 0, 0.2) !important;
          border-top: 2px solid #ff9900 !important;
        }
        .agenda-calendar .weekend-saturday {
          background-color: #ff6b7208 !important;
        }
        .agenda-calendar .weekend-sunday {
          background-color: #ff6b7212 !important;
        }
        .agenda-calendar .past-date {
          color: rgba(0, 0, 0, 0.15) !important;
          background-color: rgba(0, 0, 0, 0.02) !important;
          cursor: default !important;
        }
        .agenda-calendar .ant-picker-cell-selected .past-date {
          background-color: rgba(0, 0, 0, 0.02) !important;
        }
        .agenda-calendar .clickable-event-item {
          cursor: pointer !important;
          border-radius: 4px;
        }
        .agenda-calendar .clickable-event-item:hover {
          background-color: rgba(0, 0, 0, 0.04);
        }
        .agenda-calendar .ant-picker-cell {
          padding: 0 !important;
        }
        .agenda-calendar .ant-picker-cell-inner {
          padding-left: 0 !important;
          padding-right: 0 !important;
          margin: 0 !important;
        }
        .agenda-calendar .ant-picker-calendar-date-value {
          padding: 2px 12px !important;
        }
        .agenda-calendar .ant-picker-calendar-date-content {
          padding: 2px 12px !important;
        }
        .agenda-calendar .calendar-event {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .agenda-calendar .calendar-event li {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 11px;
          line-height: 18px;
        }
        .agenda-calendar-mobile .ant-picker-content thead {
          display: none !important;
        }
        .agenda-calendar-mobile .ant-picker-content tbody,
        .agenda-calendar-mobile .ant-picker-content tbody tr,
        .agenda-calendar-mobile .ant-picker-content tbody td {
          display: block !important;
          width: 100% !important;
        }
        .agenda-calendar-mobile .ant-picker-cell:not(.ant-picker-cell-in-view) {
          display: none !important;
        }
        .agenda-calendar-compact-scroll .ant-picker-content tbody {
          display: block !important;
          max-height: 640px;
          overflow-y: auto;
        }
        .agenda-calendar-compact-scroll .ant-picker-content tbody tr {
          display: block !important;
        }
        .agenda-calendar-compact-scroll .ant-picker-content tbody td {
          display: block !important;
          width: 100% !important;
        }
        .agenda-list-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          border-radius: 8px;
          margin-bottom: 10px;
          margin-left: 20px;
          background-color: rgba(0, 0, 0, 0.02);
          border: 1px solid rgba(0, 0, 0, 0.06);
        }
        .agenda-list-actions {
          opacity: 0;
          transition: all 0.15s ease-in-out;
          display: flex;
          gap: 10px;
          margin-left: 8px;
        }
        .agenda-list-item:hover .agenda-list-actions {
          opacity: 1;
        }
        .agenda-list-actions .anticon:hover {
          color: #ff4d4f !important;
        }
      `}</style>

      {renderLayout()}

      <AgendaEventModal
        show={showModal}
        setShow={setShowModal}
        event={editingEvent}
        selectedDate={selectedDate}
        onSave={handleSaveEvent}
        isViewOnly={isViewOnly}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AgendaTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildUpcomingEvents(events) {
  const today = dayjs().format('YYYY-MM-DD');
  const upcoming = events
    .filter(e => e.date >= today)
    .sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });

  // Group by date
  const grouped = [];
  let currentDate = null;
  let currentGroup = null;

  for (const evt of upcoming) {
    if (evt.date !== currentDate) {
      currentDate = evt.date;
      currentGroup = { date: evt.date, items: [] };
      grouped.push(currentGroup);
    }
    currentGroup.items.push(evt);
  }

  return grouped;
}

function buildEventsByDate(events) {
  const map = {};
  for (const evt of events) {
    if (!map[evt.date]) map[evt.date] = [];
    map[evt.date].push(evt);
  }
  return map;
}

function formatTimeRange(start, end) {
  if (!start && !end) return '';
  if (start && end) return `${start} - ${end}`;
  return start || end;
}
